<template>
  <div class="search">
    <span class="tooltip" v-if="showTooltip">{{showTooltip}}</span>

    <md-field>
      <div class="custom-field">
        <md-input v-model="searchValue" :placeholder="placeholder"
                  @keyup.enter="validateSearch(false)" />

        <span class="copy-btn" v-if="showCopyLink" @click="copySearchLink">
          <i class="fa fa-link" />
          Copy link
        </span>
        <span class="input-suffix reset-search" @click="resetSearch" v-if="searchValue || searchTerm">
          <i class="fa fa-times-circle" />
        </span>
        <span class="input-suffix no-border-radius" v-if="suffix && searchValue && (/^[a-zA-Z.]+/).test(searchValue)">
          <b>@{{searchValue.startsWith('hypto.') ? 'icici' : 'yesbank'}}</b>
        </span>
        <span class="input-suffix primary-bg" @click="validateSearch(false)">
          <i class="fa fa-search" />
        </span>
      </div>
    </md-field>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import { copyText } from '@/utils/helpers'

export default {
  name: 'SearchRecords',
  props: {
    triggerSearch: Function,
    placeholder: String,
    searchTerm: {
      type: String,
      default: null
    },
    suffix: {
      type: Boolean,
      default: false
    },
    patterns: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      searchValue: null,
      showTooltip: null,
      errorMsg: null,
      showCopyLink: false
    }
  },
  created () {
    this.$emit('setFunctions', this.searchFunctions)
  },
  watch: {
    searchTerm (newVal) {
      if (newVal === null) {
        this.searchValue = null
      }
    },
    searchValue () {
      this.showCopyLink = false
    }
  },
  methods: {
    copyText,
    ...mapMutations(['SET_SEARCH_VALUE']),
    searchFunctions () {
      return {
        updateQuery: query => {
          this.searchValue = query
          this.validateSearch(true, query)
        },
        setError: msg => {
          this.errorMsg = msg
          this.searchValue = null
          this.showTooltip = null
        },
        getError: () => {
          return this.errorMsg
        }
      }
    },
    resetSearch () {
      this.searchValue = null
      if (this.searchTerm) {
        this.$emit('populateList')
      }
    },
    queryValidateSearch (query) {
      const arr = query.split('@')
      this.searchValue = arr[0]
      const bank = arr[1]

      if (!((bank === 'yesbank' && new RegExp(`^[a-z]+[0-9]*(${process.env.NODE_ENV})?(@yesbank)?$`).test(query)) ||
          (bank === 'icici' && new RegExp(`^hypto.[a-z]+[0-9]*(${process.env.NODE_ENV})?(@icici)?$`).test(query)))) {
        this.showTooltip = 'Please enter a valid UPI ID'
      }
    },
    validateSearch (initial, query) {
      if (this.suffix) {
        if (initial && query.includes('@')) {
          this.queryValidateSearch(query)
        }
        if (!/^[a-zA-Z0-9.]*$/.test(this.searchValue) && !initial) {
          this.showTooltip = 'Only alphanumeric values and dot is allowed'
        } else if (!(new RegExp(`^(hypto)?.?[a-zA-Z]+[0-9]*(${process.env.NODE_ENV})?$`).test(this.searchValue) || (/^[0-9]+$/).test(this.searchValue))) {
          this.showTooltip = 'Please enter valid ID or UPI ID'
        }

        if (!this.showTooltip) {
          this.searchRecord(initial)
        }

        setTimeout(() => {
          this.showTooltip = null
        }, 3000)
      } else if (this.patterns.length) {
        let valid = false
        for (let pattern of this.patterns) {
          if (pattern(this.searchValue)) {
            valid = true
            this.searchRecord(initial)
            break
          }
        }

        if (!valid) {
          this.showTooltip = 'Please enter valid search pattern'
          setTimeout(() => {
            this.showTooltip = null
          }, 3000)
        }
      } else if (this.searchValue) {
        this.searchRecord(initial)
      }

      if (!initial) {
        this.$router.replace({query: {}})
      } else if (this.showTooltip) {
        this.searchFunctions().setError(this.showTooltip)
      }
    },
    searchRecord (initial) {
      this.SET_SEARCH_VALUE(this.searchValue)
      this.triggerSearch(initial)
      this.$nextTick(() => {
        this.showCopyLink = true
      })
    },
    copySearchLink () {
      let copyUrl = new URL(location.pathname, location.origin)
      copyUrl.searchParams.set('q', encodeURIComponent(this.searchValue))
      this.copyText(copyUrl.href, {text: 'Link copied', selector: '.search .copy-btn'})
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/scss/customField.scss';

  .search {
    @include custom-flex();
    position: relative;

    .tooltip {
      position: absolute;
      top: -29px;
      left: 10px;
      background: rgba(1, 1, 1, 0.5);
      color: $white;
      padding: 5px 8px;
      border-radius: 5px;
      z-index: 10;
      font-size: 13px;
      font-weight: 500;
      white-space: nowrap;
    }

    .md-field {
      margin: 0 !important;

      .md-input {
        border-radius: 5px 0 0 5px !important;
        border: none !important;
        text-overflow: ellipsis;

        &::placeholder {
          text-overflow: unset;
        }
      }

      .custom-field {
        border-radius: 10px;
        border: 1px solid $grey-outline;

        .copy-btn {
          padding: 2px 8px;
          margin: 6px 0;
          background: $grey-outline;
          color: $grey-text;
          border-radius: 25px;
          font-size: 12px;
          cursor: pointer;

          i {
            padding: 0 !important;
            font-size: 11px !important;
            margin-right: 3px;
          }
        }

        .input-suffix {
          border-left: none;

          &.reset-search {
            background: transparent !important;

            .fa {
              color: $secondary !important;
            }
          }

          &.primary-bg {
            background: $primary !important;
            color: #fff;
            border-left: none;
          }

          &.no-border-radius {
            border-radius: 0 !important;
          }

          b {
            padding: 0 10px;
            font-size: 13px;
          }
        }
      }
    }
  }

  .mobile, .tablet {
    .search {
      margin-top: 10px;

      .tooltip {
        top: -33px !important;
        left: 0px !important;
      }
    }
  }

  .desktop {
    .search {
      margin-right: 8px;

      .custom-field {
        min-width: 320px;
      }
    }
  }
</style>
